import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, TrendingUp, Shield, Target, ChartBar, Sparkles, Check, MessageSquare, FileText, Zap } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';

// Floating Orb Component for background effects
const FloatingOrb = ({ className, delay = 0, size = 'md' }) => {
  const sizes = {
    sm: 'w-32 h-32',
    md: 'w-64 h-64',
    lg: 'w-96 h-96',
    xl: 'w-[500px] h-[500px]'
  };
  
  return (
    <motion.div
      className={`absolute rounded-full blur-3xl opacity-30 ${sizes[size]} ${className}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ 
        opacity: [0.2, 0.4, 0.2],
        scale: [1, 1.1, 1],
      }}
      transition={{
        duration: 8,
        delay,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    />
  );
};

// Glassmorphism Card Component
const GlassCard = ({ children, className = '', hover = true }) => (
  <motion.div
    className={`
      relative overflow-hidden rounded-2xl
      bg-white/70 dark:bg-white/5
      backdrop-blur-xl backdrop-saturate-150
      border border-white/20 dark:border-white/10
      shadow-[0_8px_32px_rgba(0,0,0,0.08)]
      ${hover ? 'hover:shadow-[0_16px_48px_rgba(0,0,0,0.12)] hover:border-white/30 dark:hover:border-white/20' : ''}
      transition-all duration-500 ease-out
      ${className}
    `}
    whileHover={hover ? { y: -8, scale: 1.02 } : {}}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
  >
    {children}
  </motion.div>
);

// Animated Feature Icon
const FeatureIcon = ({ icon: Icon, color }) => (
  <motion.div 
    className={`
      relative w-16 h-16 rounded-2xl flex items-center justify-center
      bg-gradient-to-br ${color}
      shadow-lg
    `}
    whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
    transition={{ duration: 0.5 }}
  >
    <Icon className="w-8 h-8 text-white" />
    <div className="absolute inset-0 rounded-2xl bg-white/20 opacity-0 hover:opacity-100 transition-opacity" />
  </motion.div>
);

// Step Card for How It Works
const StepCard = ({ number, title, description, icon: Icon, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    viewport={{ once: true }}
    className="relative"
  >
    <GlassCard className="p-8 h-full">
      <div className="flex flex-col items-center text-center">
        <motion.div 
          className="relative mb-6"
          whileHover={{ scale: 1.1 }}
          transition={{ type: "spring", stiffness: 400 }}
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-emerald-400 flex items-center justify-center shadow-lg shadow-primary/25">
            <Icon className="w-10 h-10 text-white" />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center text-sm font-bold">
            {number}
          </div>
        </motion.div>
        <h3 className="text-xl font-bold mb-3">{title}</h3>
        <p className="text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </GlassCard>
  </motion.div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.3], [1, 0.95]);

  const features = [
    {
      icon: Target,
      title: "Personalized Plans",
      description: "AI-crafted investment allocations tailored to your unique goals, timeline, and risk appetite",
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: ChartBar,
      title: "Growth Projections",
      description: "Visualize your wealth trajectory with data-driven 5 and 10-year forecasts",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Shield,
      title: "Tax Optimization",
      description: "Maximize returns through intelligent tax-advantaged account strategies",
      color: "from-violet-500 to-purple-500"
    },
    {
      icon: TrendingUp,
      title: "ETF Recommendations",
      description: "Low-cost, diversified portfolios using premium ETFs for maximum efficiency",
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: Sparkles,
      title: "AI Chat Assistant",
      description: "Get instant, intelligent answers about your investment strategy anytime",
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: Zap,
      title: "Real-time Updates",
      description: "Dynamic plan adjustments that evolve with your changing financial situation",
      color: "from-indigo-500 to-blue-500"
    }
  ];

  const howItWorksSteps = [
    {
      icon: FileText,
      title: "Answer Questions",
      description: "Complete a quick questionnaire about your financial goals, risk tolerance, and current situation"
    },
    {
      icon: Sparkles,
      title: "AI Generates Plan",
      description: "Our advanced AI analyzes your profile and creates a personalized, phase-based investment strategy"
    },
    {
      icon: MessageSquare,
      title: "Refine & Execute",
      description: "Chat with AI to fine-tune your plan, then start investing with confidence"
    }
  ];

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Animated Background Orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <FloatingOrb className="bg-primary/40 -top-20 -left-20" size="lg" delay={0} />
        <FloatingOrb className="bg-emerald-400/30 top-1/3 -right-32" size="xl" delay={2} />
        <FloatingOrb className="bg-cyan-400/20 bottom-20 left-1/4" size="md" delay={4} />
        <FloatingOrb className="bg-violet-400/20 top-2/3 right-1/4" size="sm" delay={1} />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/60 backdrop-blur-2xl backdrop-saturate-150 border-b border-border/40">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex justify-between items-center">
          <motion.div 
            className="flex items-center gap-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.img 
              src="/logo.svg" 
              alt="Orbit" 
              className="w-12 h-12"
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            />
            <span className="text-3xl font-bold" style={{ fontFamily: "'Apple Garamond', serif" }}>Orbit</span>
          </motion.div>
          <motion.div 
            className="flex gap-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Button 
              variant="ghost" 
              onClick={() => navigate('/demo')} 
              data-testid="demo-button"
              className="hover:bg-primary/10 transition-all duration-300"
            >
              View Demo
            </Button>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                onClick={() => navigate('/login')} 
                data-testid="get-started-button"
                className="bg-gradient-to-r from-primary to-emerald-500 hover:from-primary/90 hover:to-emerald-500/90 shadow-lg shadow-primary/25 transition-all duration-300"
              >
                Get Started
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </header>

      {/* Hero Section */}
      <motion.section 
        className="relative py-20 md:py-32" 
        data-testid="hero-section"
        style={{ opacity: heroOpacity, scale: heroScale }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6"
              >
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">AI-Powered Financial Planning</span>
              </motion.div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.1]">
                <span className="block">Your Financial</span>
                <span className="block bg-gradient-to-r from-primary via-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                  Future, Simplified
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-xl">
                Get a personalized, phase-based investment plan crafted by AI. Clear recommendations, actionable steps, zero jargon.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button 
                    size="lg" 
                    onClick={() => navigate('/login')} 
                    className="text-lg px-8 py-6 bg-gradient-to-r from-primary to-emerald-500 hover:from-primary/90 hover:to-emerald-500/90 shadow-xl shadow-primary/30 group transition-all duration-300" 
                    data-testid="hero-get-started"
                  >
                    Get Your Plan 
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    onClick={() => navigate('/demo')} 
                    className="text-lg px-8 py-6 border-2 hover:bg-muted/50 transition-all duration-300" 
                    data-testid="hero-view-demo"
                  >
                    See Demo
                  </Button>
                </motion.div>
              </div>
              
              <div className="flex flex-wrap items-center gap-6 text-sm">
                {[
                  { icon: Shield, text: "Bank-level Security" },
                  { icon: Sparkles, text: "AI-Powered" },
                  { icon: Check, text: "Free to Start" }
                ].map((item, idx) => (
                  <motion.div 
                    key={idx}
                    className="flex items-center gap-2 text-muted-foreground"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + idx * 0.1 }}
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-primary" />
                    </div>
                    <span>{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Hero Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotateY: -10 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              {/* Orbital rings animation */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div 
                  className="absolute w-[400px] h-[400px] rounded-full border border-primary/20"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                />
                <motion.div 
                  className="absolute w-[500px] h-[500px] rounded-full border border-emerald-400/10"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                />
              </div>
              
              {/* Main image card */}
              <GlassCard className="relative z-10 p-3" hover={false}>
                <img 
                  src="https://images.unsplash.com/photo-1642790106117-e829e14a795f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBkYXNoYm9hcmQlMjBkYXJrfGVufDB8fHx8MTcwMDAwMDAwMHww&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Orbit Dashboard Preview"
                  className="rounded-xl w-full aspect-[4/3] object-cover"
                />
                
                {/* Floating stat cards */}
                <motion.div
                  className="absolute -left-8 top-1/4 z-20"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <GlassCard className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Portfolio Growth</div>
                        <div className="font-bold text-emerald-500">+24.5%</div>
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>
                
                <motion.div
                  className="absolute -right-6 bottom-1/4 z-20"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  <GlassCard className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                        <Target className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Goal Progress</div>
                        <div className="font-bold text-violet-500">78%</div>
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* How It Works Section */}
      <section className="py-20 md:py-32 relative" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Simple Process
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              How It Works
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Get your personalized investment plan in three simple steps
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-1/2 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-primary/50 via-emerald-400/50 to-primary/50 -translate-y-1/2" />
            
            {howItWorksSteps.map((step, idx) => (
              <StepCard
                key={idx}
                number={idx + 1}
                title={step.title}
                description={step.description}
                icon={step.icon}
                delay={idx * 0.2}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-muted/30 to-background relative" data-testid="features-section">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-emerald-400/5 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Features
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Everything You Need to{' '}
              <span className="bg-gradient-to-r from-primary to-emerald-400 bg-clip-text text-transparent">
                Invest Smart
              </span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful tools and insights to help you build wealth with confidence
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="p-8 h-full group" data-testid={`feature-card-${idx}`}>
                  <FeatureIcon icon={feature.icon} color={feature.color} />
                  <h3 className="text-xl font-semibold mt-6 mb-3 group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 relative overflow-hidden" data-testid="cta-section">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-emerald-500 to-teal-500" />
        
        {/* Animated pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        {/* Floating orbs */}
        <motion.div
          className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white/10 blur-2xl"
          animate={{ y: [0, 30, 0], x: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-white/10 blur-2xl"
          animate={{ y: [0, -30, 0], x: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
              Ready to Take Control of Your{' '}
              <span className="relative">
                Financial Future?
                <motion.span
                  className="absolute -bottom-2 left-0 right-0 h-1 bg-white/40 rounded-full"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                />
              </span>
            </h2>
            <p className="text-lg md:text-xl mb-10 text-white/90">
              Join thousands who've already taken the first step toward financial freedom
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg" 
                  onClick={() => navigate('/login')} 
                  className="text-lg px-10 py-6 bg-white text-primary hover:bg-white/90 shadow-2xl shadow-black/20 group" 
                  data-testid="cta-get-started"
                >
                  Get Started Free 
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={() => navigate('/demo')} 
                  className="text-lg px-10 py-6 bg-transparent border-2 border-white text-white hover:bg-white/10" 
                  data-testid="cta-view-demo"
                >
                  View Demo
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <motion.div 
              className="flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
            >
              <img src="/logo.svg" alt="Orbit" className="w-10 h-10" />
              <span className="text-2xl font-bold" style={{ fontFamily: "'Apple Garamond', serif" }}>Orbit</span>
            </motion.div>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              © 2025 Orbit. Investment recommendations for educational purposes. Consult a financial advisor for personalized advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
